import { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Appointment.css";
import { useNavigate } from "react-router-dom";


// Simple UI Components
const Button = ({ children, onClick, disabled, type = "button", className = "", variant = "default" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`btn ${variant === "default" ? "btn-primary" : "btn-ghost"} ${className}`}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => <div className={`card ${className}`}>{children}</div>;

const CardHeader = ({ children }) => <div className="card-header">{children}</div>;
const CardTitle = ({ children, className = "" }) => <h3 className={`card-title ${className}`}>{children}</h3>;
const CardContent = ({ children, className = "" }) => <div className={`card-content ${className}`}>{children}</div>;

const Input = ({ type, value, onChange, min, className = "", placeholder }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    min={min}
    className={`input ${className}`}
    placeholder={placeholder}
  />
);

const Select = ({ children, value, onValueChange }) => (
  <select value={value} onChange={(e) => onValueChange(e.target.value)} className="select">
    {children}
  </select>
);

const SelectItem = ({ children, value }) => <option value={value}>{children}</option>;

const Textarea = ({ placeholder, value, onChange, className = "" }) => (
  <textarea placeholder={placeholder} value={value} onChange={onChange} className={`textarea ${className}`} />
);

const Badge = ({ children, className = "" }) => <span className={`badge ${className}`}>{children}</span>;

// Enhanced icons with more visual appeal
const Icons = {
  Calendar: () => <span className="icon-bounce">📅</span>,
  Clock: () => <span className="icon-pulse">🕐</span>,
  User: () => <span className="icon-bounce">👤</span>,
  MessageSquare: () => <span className="icon-pulse">💬</span>,
  CreditCard: () => <span className="icon-bounce">💳</span>,
  CheckCircle: () => <span className="icon-success">✅</span>,
  Loader2: () => <span className="icon-spin">⏳</span>,
  Stethoscope: () => <span className="icon-pulse">🩺</span>,
  Heart: () => <span className="icon-heartbeat">❤️</span>,
  Sparkles: () => <span className="icon-sparkle">✨</span>,
  ArrowRight: () => <span className="icon-slide">→</span>,
};

function Appointment() {
  const [hmsServiceId, setHmsServiceId] = useState("");
  const [retEndpoint, setRetEndpoint] = useState("");
  const [formData, setFormData] = useState({
    doctor: "",
    appointmentDate: "",
    timeZoneId: "",
    timeSlot: "",
    message: "",
  });

  const [doctors, setDoctors] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("Please select a doctor and date to view available time zones");
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const messageTimerRef = useRef(null);

const [encoded, setEncoded] = useState("");

const navigate = useNavigate();
  useEffect(() => {
    // Step 1: Get encoded string from hash
    const hashValue = window.location.hash.substring(1);
    console.log(hashValue,'hashValue')
    setEncoded(hashValue);

    if (hashValue) {
      try {
        // Step 2: Decode internally
        const decodedUrl = atob(hashValue);
         console.log("Decoded (internal use):", decodedUrl);

        // Step 3: Extract query params from decoded URL
        const urlObj = new URL(decodedUrl);
        const queryParams = new URLSearchParams(urlObj.search);

        const serviceId = queryParams.get("hms_service_id");
        const endpoint = queryParams.get("ret_endpoint");

        if (serviceId) {
          setHmsServiceId(serviceId);
           console.log("HMS Service ID:", serviceId);
        } else {
          console.error("No HMS Service ID found in URL");
          toast.error("Missing HMS Service ID parameter");
        }

        if (endpoint) {
          setRetEndpoint(endpoint);
           console.log("Return Endpoint:", endpoint);
        }

        
      } catch (err) {
        console.error("Invalid encoded string");
      }
    }
  }, []);

  useEffect(() => {
    if (hmsServiceId) {
      const initialize = async () => {
        try {
          await authenticateUser();
          await fetchDoctors();
        } catch (error) {
          console.error("Initialization failed:", error);
        }
      };

      initialize();
    }

    return () => {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }
    };
  }, [hmsServiceId]);

  const authenticateUser = async () => {
    try {
      const response = await fetch(
        `https://hms-g2gbera0bddsdugv.canadacentral-01.azurewebsites.net/auth/?hms_service_id=${hmsServiceId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      console.log(data);

      if (response.ok) {
        localStorage.setItem("access_token", data.access_token);
        if (retEndpoint) {
          localStorage.setItem("ret_endpoint", retEndpoint);
        }
        console.log("Token received and stored successfully");
        // toast.success("Authentication successful!");
        return true;
      } else {
        toast.error(data.message || "Authentication failed");
        throw new Error(data.message || "Authentication failed");
      }
    } catch (error) {
      console.error("Error during authentication:", error);
      toast.error("Error occurred during authentication");
      throw error;
    }
  };

  const API_BASE_URL = "https://hms-g2gbera0bddsdugv.canadacentral-01.azurewebsites.net";

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_BASE_URL}/appointment/doctors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch doctors");
      }

      const data = await response.json();
      console.log(data);  

      setDoctors(data.doctors || []);
      // toast.success(`${data.doctors.length} doctors available`);
    } catch (error) {
      setMessage("Error loading doctors. Please try again later.");
      toast.error("Error loading doctors. Please try again later.");
      console.error("Error fetching doctors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    if (field === "appointmentDate" || field === "doctor") {
      setFormData((prev) => ({
        ...prev,
        timeZoneId: "",
        timeSlot: "",
        [field]: value,
      }));

      setTimeSlots([]);
      setCurrentStep(field === "doctor" && value ? 2 : 1);

      const doctorValue = field === "doctor" ? value : formData.doctor;
      const dateValue = field === "appointmentDate" ? value : formData.appointmentDate;

      if (doctorValue && dateValue) {
        fetchTimeZones(doctorValue, dateValue, formData.message);
      }
    } else if (field === "message") {
      if (messageTimerRef.current) {
        clearTimeout(messageTimerRef.current);
      }

      if (formData.doctor && formData.appointmentDate) {
        messageTimerRef.current = setTimeout(() => {
          fetchTimeZones(formData.doctor, formData.appointmentDate, value);
        }, 500);
      }
    }
  };

  const handleTimeZoneChange = (timeZoneId, timeZoneText) => {
    setFormData({
      ...formData,
      timeZoneId: timeZoneId,
      timeZoneText: timeZoneText,
      timeSlot: "",
    });

    setCurrentStep(3);
    fetchDetailedTimeSlots(formData.doctor, formData.appointmentDate, timeZoneId);
    // toast.success(`Time zone "${timeZoneText}" selected`);
  };

  const handleTimeSlotChange = (slotId, slotText) => {
    setFormData({
      ...formData,
      timeSlot: slotId,
      timeSlotText: slotText,
    });
    setCurrentStep(4);
    // toast.success(`Time slot "${slotText}" selected`);
  };

  const fetchTimeZones = async (doctorId, date, note = "") => {
    if (!doctorId || !date) {
      setMessage(!date ? "Please select a date to view available time zones" : "Please select a doctor first");
      setTimeZones([]);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("access_token");

      const response = await fetch(`${API_BASE_URL}/appointment/timezones`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newdoctor: Number.parseInt(doctorId),
          newdate: date,
          note: note || "",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch time zones");
      }

      const data = await response.json();
      console.log("Time zones data:", data);
      localStorage.setItem("org_name", data.org_name);
      localStorage.setItem("razorpay_key", data.razorpay_key);

      if (data.activity_id && data.activity_amount) {
        setAppointmentDetails({
          doctor_id: data.doctor_id,
          docname: data.docname,
          activity_id: data.activity_id,
          activity_amount: data.activity_amount,
          activity_name: data.activity_name || "Consultation",
        });
      }

      const zones = data.avtz || [];
      setTimeZones(zones);

      if (zones.length === 0) {
        setMessage("No time zones available for the selected date");
        toast.warning("No time zones available for the selected date");
      } else {
        if (!note || note === "") {
          // toast.info(`${zones.length} time zones available`);
        }
      }
    } catch (error) {
      setMessage(`Error loading time zones. Please try again later.`);
      toast.error(`Error loading time zones: ${error}`);
      console.error("Error fetching time zones:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedTimeSlots = async (doctorId, date, timeZoneId) => {
    if (!doctorId || !date || !timeZoneId) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("access_token");

      const payload = {
        doctor_id: Number.parseInt(doctorId),
        tzid: Number.parseInt(timeZoneId),
        docname: appointmentDetails?.docname || "",
        newdate: date,
        note: formData.message || "",
        activity_name: appointmentDetails?.activity_name || "Consultation",
        activity_id: appointmentDetails?.activity_id || 0,
        activity_amount: appointmentDetails?.activity_amount || 0,
      };
      console.log(payload);

      const response = await fetch(`${API_BASE_URL}/appointment/timeslots`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch time slots");
      }

      const data = await response.json();
      console.log("Detailed time slots data:", data);

      if (data.activity_id && data.activity_amount) {
        setAppointmentDetails({
          doctor_id: data.doctor_id,
          docname: data.docname,
          activity_id: data.activity_id,
          activity_amount: data.activity_amount,
          activity_name: data.activity_name || "Consultation",
        });
      }

      const slots = data.avslots || [];
      setTimeSlots(slots);

      if (slots.length === 0) {
        setMessage("No detailed time slots available for the selected time zone");
        toast.warning("No time slots available for the selected time zone");
      } else {
        // toast.info(`${slots.length} time slots available`);
      }
    } catch (error) {
      setMessage(`Error loading detailed time slots. Please try again later.`);
      toast.error(`Error loading time slots: ${error}`);
      console.error("Error fetching detailed time slots:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.timeSlot) {
      toast.warn("Please select an available time slot");
      return;
    }

    try {
      setLoading(true);

      const selectedSlot = timeSlots.find((slot) => slot.id === Number.parseInt(formData.timeSlot));
      const slotFrom = selectedSlot?.from_time || "";
      const slotTo = selectedSlot?.to_time || "";
      console.log(formData);

      const queryParams = new URLSearchParams({
        doctor_id: formData.doctor,
        activity_amount: appointmentDetails?.activity_amount?.toString() || "",
        tzid: formData.timeZoneId || "",
        activity_name: appointmentDetails?.activity_name || "Consultation",
        activity_id: appointmentDetails?.activity_id?.toString() || "",
        docname: appointmentDetails?.docname || "",
        newdate: formData.appointmentDate,
        slotid: formData.timeSlot,
        slotfrom: slotFrom,
        slotto: slotTo,
        note: formData.message || "",
        addrel: "no",
      });

     navigate(`/book_appointment/user_detail?${queryParams.toString()}`);
    } catch (error) {
      toast.error(`Error processing form: ${error}`);
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedDoctor = doctors.find((d) => d.id.toString() === formData.doctor);

  return (
    <div  className="appointment-booking">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="container">
        <div className="header">
          <div className="brand-logo">
            <img src="./hlogo.png" alt="HealTrail Logo" className="logo-img" />
          </div>
          <div>
            <h1> Appointments by HealTrail</h1>
            <p style={{color: '#fbfafaff'}}>
               Your Health Journey Starts Here
            </p>
          </div>
        </div>

        <div className="progress-steps">
          {[
            { step: 1, icon: Icons.User, label: "Doctor" },
            { step: 2, icon: Icons.Calendar, label: "Date" },
            { step: 3, icon: Icons.Clock, label: "Time" },
            { step: 4, icon: Icons.CheckCircle, label: "Confirm" },
          ].map(({ step, icon: Icon, label }, index) => (
            <div key={step} className="progress-step">
              <div className={`step-icon ${currentStep >= step ? "active" : ""}`}>
                <Icon />
              </div>
              <span className={`step-label ${currentStep >= step ? "active" : ""}`}>{label}</span>
              {index < 3 && <Icons.ArrowRight />}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="appointment-form">
          <div className="form-grid">
            <Card className="form-card">
              <CardHeader>
                <CardTitle>
                  <Icons.User /> Select Doctor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={formData.doctor} onValueChange={(value) => handleChange("doctor", value)}>
                  <option value="">Choose your doctor</option>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id.toString()}>
                      {doctor.doctor_name}
                    </SelectItem>
                  ))}
                </Select>
              </CardContent>
            </Card>

            <Card className="form-card">
              <CardHeader>
                <CardTitle>
                  <Icons.Calendar /> Appointment Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  type="date"
                  value={formData.appointmentDate}
                  onChange={(e) => handleChange("appointmentDate", e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
              </CardContent>
            </Card>

            <Card className="form-card">
              <CardHeader>
                <CardTitle>
                  <Icons.MessageSquare /> Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Describe your symptoms..."
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                />
              </CardContent>
            </Card>
          </div>

          {(timeZones.length > 0 || (formData.timeZoneId && timeSlots.length > 0)) && (
            <div className="time-selection">
              {timeZones.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <Icons.Clock /> Time Zones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="time-zones">
                      {timeZones.map((zone) => (
                        <Button
                          key={zone.id}
                          type="button"
                          variant={formData.timeZoneId === zone.id.toString() ? "default" : "ghost"}
                          onClick={() => handleTimeZoneChange(zone.id.toString(), zone.slot_timezone)}
                        >
                          {zone.slot_timezone}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {formData.timeZoneId && timeSlots.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <Icons.Clock /> Available Slots
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="time-slots">
                      {timeSlots.map((slot) => (
                        <Button
                          key={slot.id}
                          type="button"
                          variant={formData.timeSlot === slot.id.toString() ? "default" : "ghost"}
                          onClick={() => handleTimeSlotChange(slot.id.toString(), slot.slot)}
                        >
                          {slot.slot}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {appointmentDetails && (
            <Card className="appointment-summary">
              <CardHeader>
                <CardTitle>
                  <Icons.CreditCard /> Appointment Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="summary-grid">
                  <div>
                    <span>Doctor</span>
                    <p>{appointmentDetails.docname}</p>
                  </div>
                  <div>
                    <span>Service</span>
                    <p>{appointmentDetails.activity_name}</p>
                  </div>
                  <div>
                    <span>Date</span>
                    <p>{formData.appointmentDate}</p>
                  </div>
                  <div>
                    <span>Fee</span>
                    <p className="fee">₹{appointmentDetails.activity_amount}</p>
                  </div>
                </div>
                {formData.timeSlot && (
                  <div className="selected-time">
                    <span>Selected Time:</span>
                    <Badge>{formData.timeSlotText}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="submit-section">
            <Button type="submit" disabled={loading || !formData.timeSlot} className="submit-btn">
              {loading ? (
                <>
                   Schedule Appointment
                </>
              ) : (
                <>
                  Schedule Appointment 
                </>
              )}
            </Button>
          </div>

          {loading && (
            <div className="loading-message">
              <Icons.Loader2 /> Loading...
            </div>
          )}

          {!loading && timeZones.length === 0 && message && (
            <div className="info-message">
              <p>{message}</p>
            </div>
          )}
        </form>

        <div className="footer">
          <p style={{color: '#f3f1f1de'}}>
            Powered By{" "}
            <a  href="https://www.healtrail.com/" target="_blank" rel="noreferrer">
              HealTrail
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Appointment;